import type { CreateInventoryAdjustmentRequest } from '@/domains/adjustment-groups/types/requests/CreateInventoryAdjustmentRequest.types'
import type { CompositionModel } from '@/domains/inventory/types/models/Composition.types'
import type { FamilyStockLeaf } from '@/domains/inventory/types/models/FamilyStock.types'
import type { CreateInventoryItemRequest } from '@/domains/inventory/types/requests/CreateInventoryItemRequest.types'
import type { DraftReplenishmentRequest } from '@/domains/inventory/types/requests/DraftReplenishmentRequest.types'
import type { MoveStockRequest } from '@/domains/inventory/types/requests/MoveStockRequest.types'
import type { RunCompositionRequest } from '@/domains/inventory/types/requests/RunCompositionRequest.types'
import type { SetInventoryItemReorderRequest } from '@/domains/inventory/types/requests/SetInventoryItemReorderRequest.types'
import type { UpdateReplenishmentSettingsRequest } from '@/domains/inventory/types/requests/UpdateReplenishmentSettingsRequest.types'
import type { UpsertCompositionRequest } from '@/domains/inventory/types/requests/UpsertCompositionRequest.types'
import type { DraftReplenishmentResponse } from '@/domains/inventory/types/responses/DraftReplenishmentResponse.types'
import type {
  AcceptedDraftRun,
  DraftOutcome,
  DraftRunProgress
} from '@/domains/inventory/types/responses/DraftRunResponse.types'
import type { InventoryItemResponse } from '@/domains/inventory/types/responses/InventoryItemResponse.types'
import type { MoveStockResponse } from '@/domains/inventory/types/responses/MoveStockResponse.types'
import type { ReplenishmentSettingsResponse } from '@/domains/inventory/types/responses/ReplenishmentSettingsResponse.types'
import type { SourceLocationOption } from '@/domains/inventory/types/responses/SourceLocationResponse.types'
import type { PaginatedResponse } from '@/domains/shared/types/pagination/responses/PaginatedResponse.types'
import type { IndexProductVariant } from '@/domains/variants/types/models/IndexProductVariant.types'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil'

/**
 * The status the drafting endpoint answers with when it started a background run instead of
 * committing in the request. Named here because this collection is the only place allowed to
 * know it — callers branch on `DraftOutcome.accepted`.
 */
const HTTP_ACCEPTED = 202

/**
 * Replenishment API Collection
 *
 * Backend Controllers: App\Admin\Http\Domains\Inventory\Controllers\Replenishment\DraftReplenishmentController
 *                      App\Admin\Http\Domains\Inventory\Controllers\Replenishment\DraftRunController
 */
export const callReplenishment = () => {
  /**
   * Turns the buyer's selection into draft purchase and transfer orders, grouped by the
   * source each line resolves to.
   *
   * AJAX rather than an Inertia visit because the controller returns the result DTO as
   * JSON, not an Inertia response — deliberately, so the admin screen, the API and a
   * future MCP tool all reach the action through one door. The report's `lines` prop
   * stays an Inertia partial reload; only this action is a fetch.
   *
   * `preventConcurrent` is left at its default `true`: the token is derived from the
   * endpoint and method, so a double-pressed button aborts its own first request rather
   * than raising two sets of documents.
   *
   * Answers **two** ways, which is the whole reason this returns a union. At or below the
   * controller's line limit it commits inline and answers `200` with the result. Above it, it
   * writes the run, dispatches a job per document group and answers `202` with a run id —
   * nothing is drafted yet, and the caller has to poll. Branching on the status here keeps
   * every caller from having to.
   *
   * Rejects with an `Error` carrying the backend's `message` on a 422 — every basket
   * guard reports that way, and they are caller bugs (a repeated item, an unknown id, a
   * quantity of zero, a source that cannot be ordered from), not per-line data problems.
   * Per-line problems come back inside `failed` on a success, on either path.
   *
   * A 422 refuses the **whole basket**, which is why the table will not let an
   * undraftable row be ticked — one zero-quantity line would discard every good one.
   */
  const draft = async (body: DraftReplenishmentRequest): Promise<DraftOutcome> => {
    const { data, response } = await twineServiceClient.post<
      DraftReplenishmentResponse | AcceptedDraftRun,
      DraftReplenishmentRequest
    >({
      endpoint: relativeZiggyRoute('admin.inventory.replenishment.draft'),
      body
    })

    if (response.value?.status === HTTP_ACCEPTED) {
      return { accepted: true, run: data.value as AcceptedDraftRun }
    }

    return { accepted: false, result: data.value as DraftReplenishmentResponse }
  }

  /**
   * Where one line could be pulled from instead, and what each of those places holds.
   *
   * Read when the source drawer opens, not with the report. It is one line's question, and
   * answering it for all 100 rows of a page would be 100 answers for the one row the buyer opens.
   *
   * The stock figure is the reason this endpoint exists: drafting refuses a transfer whose source
   * cannot cover it, and without this the buyer chose a warehouse blind and learnt that after the
   * press.
   *
   * Rejects with a 404 for a line belonging to another tenant. The route binds on the tenant
   * connection, so a foreign line is not hidden, it does not exist.
   */
  const sourceLocations = async (inventoryItemId: number): Promise<SourceLocationOption[]> => {
    const { data } = await twineServiceClient.get<SourceLocationOption[]>({
      endpoint: relativeZiggyRoute('admin.inventory.replenishment.lines.source-locations', {
        inventoryItem: inventoryItemId
      })
    })

    return data.value ?? []
  }

  /**
   * Runs raised by a press that could not be answered in the request. Nested because the routes
   * are: a run only exists underneath the drafting it came from.
   */
  const draftRuns = {
    /**
     * Where a background run has got to.
     *
     * Polled, so it must stay cheap: the backend reads the answer off the run's own group rows
     * in one query, however many documents the run holds.
     *
     * `preventConcurrent` stays at its default. The poller schedules the next call only after
     * the previous one settles, so two are never in flight and the abort path is unreachable —
     * do not switch this to a fixed interval without revisiting that.
     *
     * Rejects with a 404 for a run belonging to another tenant. The route binds on the tenant
     * connection, so a foreign run is not hidden, it does not exist.
     */
    show: async (runId: string): Promise<DraftRunProgress> => {
      const { data } = await twineServiceClient.get<DraftRunProgress>({
        endpoint: relativeZiggyRoute('admin.inventory.replenishment.draft-runs.show', { run: runId })
      })

      return data.value as DraftRunProgress
    },

    /**
     * Stops a background run.
     *
     * Cooperative, not a rollback: work already committed stands, and only groups that have not
     * started are stopped. Answers with the run's own progress rather than an empty body, so the
     * caller can render what was raised before the stop without polling again.
     */
    cancel: async (runId: string): Promise<DraftRunProgress> => {
      const { data } = await twineServiceClient.post<DraftRunProgress, undefined>({
        endpoint: relativeZiggyRoute('admin.inventory.replenishment.draft-runs.cancel', { run: runId }),
        body: undefined
      })

      return data.value as DraftRunProgress
    }
  }

  /**
   * The ordered chain of sources one location replenishes from, and the two quantity keys
   * beside it. Nested because the routes are: both hang off a location.
   */
  const settings = {
    /**
     * How this location replenishes today, plus every set it may choose from.
     *
     * AJAX rather than an Inertia prop because the controller returns the response DTO as
     * JSON, not an Inertia page — so there is no route to render a page against, and the
     * chain editor is a slide-over either way.
     *
     * `has_stored_chain` is the field to read first. False means the chain returned was
     * never written: it is composed from the location's older `source` and
     * `source_location_id` keys so the screen has something true to show, and saving is what
     * turns it into a stored chain.
     *
     * Rejects with a 404 for a location belonging to another tenant. The route binds on the
     * tenant connection, so a foreign location is not hidden, it does not exist.
     */
    show: async (locationId: number): Promise<ReplenishmentSettingsResponse> => {
      const { data } = await twineServiceClient.get<ReplenishmentSettingsResponse>({
        endpoint: relativeZiggyRoute('admin.inventory.locations.replenishment.settings.show', {
          location: locationId
        })
      })

      return data.value as ReplenishmentSettingsResponse
    },

    /**
     * Replaces the chain wholesale, in the order given. There is no partial update, and an
     * empty `sources` is legitimate — it deletes every stored row and hands the location back
     * to its older keys, which is the only way to undo a stored chain.
     *
     * Answers 204, not the new state, so this resolves to nothing and the caller refetches.
     * The controller must never answer a redirect: `fetch` keeps the method when it follows a
     * 302 for anything other than POST, so a `redirect()->back()` turns one save into a second
     * `PUT` at the referring page, and that fails with a 405.
     *
     * `preventConcurrent` stays at its default. The token derives from the endpoint and
     * method, so a double-pressed Save aborts its own first request rather than writing the
     * chain twice.
     *
     * Rejects with an `Error` carrying the backend's `message` on a 422. Every guard reports
     * that way — a repeated source, a `supplier` entry carrying a location, a `location` entry
     * carrying none, an entry naming the location itself, an unregistered strategy. They are
     * `BaseException` subclasses rather than validation errors, so nothing arrives as a
     * field-keyed error bag.
     */
    update: async (locationId: number, body: UpdateReplenishmentSettingsRequest): Promise<void> => {
      await twineServiceClient.put<unknown, UpdateReplenishmentSettingsRequest>({
        endpoint: relativeZiggyRoute('admin.inventory.locations.replenishment.settings.update', {
          location: locationId
        }),
        body
      })
    }
  }

  return {
    draft,
    draftRuns,
    settings,
    sourceLocations
  }
}

/**
 * Inventory Items API Collection
 *
 * Backend Controllers: App\Admin\Http\Domains\Inventory\Controllers\InventoryAdjustmentController
 *                      App\Admin\Http\Domains\Inventory\Controllers\SetInventoryItemReorderController
 *
 * Both writes answer with the item as it now stands, so one response type and one
 * row-patching function serve every caller on the screen.
 */
export const callInventoryItems = () => {
  /**
   * Moves one item's stock, and answers with the item as it now stands.
   *
   * `adjusted_quantity` is a **delta**, never the new total. The form offers a "New" box,
   * but that box is arithmetic done in the browser — only the difference travels. Sending
   * an absolute would double the movement, because the aggregate adds what it receives to
   * the quantity it already holds.
   *
   * AJAX rather than an Inertia visit, and that is the whole reason this function exists.
   * The form lives inside the variant edit page, and an Inertia POST fires
   * `router.on('before')` — which the parent page's `useFormGuard` also listens to, because
   * that listener is registered per instance rather than per active guard. A dirty variant
   * form therefore cancelled the adjustment and put an "Unsaved Changes" dialog in front of
   * it; the child's `allowNavigation()` only stood the child's own veto down. A `fetch`
   * raises no Inertia visit, so no guard on the page can see it.
   *
   * It also stops the save being destructive. The old call carried `preserveState: false`,
   * so a successful adjustment rebuilt the page component and discarded every unsaved edit
   * in the variant form beside it.
   *
   * The item rather than 204, because the caller cannot read it back: there is no admin
   * endpoint for a single inventory item, only a Sanctum one the browser session cannot
   * call. The adjustment form also rebuilds its validation bounds from the row's quantities
   * each time it opens, so a stale row would validate the next adjustment against a number
   * the server no longer holds.
   *
   * `preventConcurrent` stays at its default `true`, but it is not a double-submit guard:
   * the client aborts the *earlier* request and sends the new one, and an aborted POST
   * already on the wire is still processed by the server. The guard against booking the
   * movement twice is the caller's — the save button disables while `isAdjusting`, and
   * `handleSave` refuses re-entry.
   *
   * Rejects with an `Error` carrying the backend's `message` on a 422. Two shapes arrive
   * there and a caller must not treat them alike — a field-keyed `errors` bag from the
   * request DTO (an unknown item, a reason outside the enum), and a bare `message` from
   * `InvalidConsignmentCorrectionQuantityException` and `InvalidConsignmentQuantityException`,
   * which are `BaseException` subclasses carrying no field at all.
   */
  const adjust = async (body: CreateInventoryAdjustmentRequest): Promise<InventoryItemResponse> => {
    const { data } = await twineServiceClient.post<InventoryItemResponse, CreateInventoryAdjustmentRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.adjustment'),
      body
    })

    return data.value as InventoryItemResponse
  }

  /**
   * Replaces the item's reorder pair, and answers with the item as it now stands.
   *
   * Both keys travel on every call, and either may be null. The projector assigns both
   * columns from one event, so omitting one does not leave it alone — it nulls it. There is
   * no partial update here, exactly as there is none for a replenishment chain.
   *
   * `null` is a real value, not a blank. A null reorder point means the merchant is not
   * replenishing this item at this location, and it is the only way to take a line off the
   * replenishment report.
   *
   * A PUT rather than a PATCH, because the pair is replaced wholesale. The controller must
   * never answer a redirect: `fetch` keeps the method when it follows a 302 for anything
   * other than POST, so a `redirect()->back()` would become a second `PUT` at the referring
   * page and fail with a 405.
   *
   * Rejects with a 404 for an item belonging to another tenant. The route binds on the
   * tenant connection, so a foreign item is not hidden, it does not exist.
   */
  const setReorderRule = async (
    inventoryItemId: number,
    body: SetInventoryItemReorderRequest
  ): Promise<InventoryItemResponse> => {
    const { data } = await twineServiceClient.put<InventoryItemResponse, SetInventoryItemReorderRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.items.reorder-rule.update', {
        inventoryItem: inventoryItemId
      }),
      body
    })

    return data.value as InventoryItemResponse
  }

  /**
   * Starts tracking stock for one variant at one location, and answers the new row.
   *
   * Its own request so that adding a location does not submit the whole variant form. It used
   * to: the adjustment form needs a persisted ulid to write against, so the stock table saved
   * the entire variant the moment a location was added — committing every other unsaved edit
   * on the page and redirecting away from it.
   *
   * Answers `Domain\Products\DataTransferObjects\InventoryItemDTO`, **not** the shape the other
   * two methods here return. That is the shape the variant page's `inventory_items` array
   * already holds, so the caller pushes the response straight in and the row is adjustable at
   * once. It is the one place in this collection where the products-side DTO is the right
   * answer, because the caller is that page's own array.
   *
   * Carries no quantity and no reorder rule. A new item opens at zero; the rule is set
   * afterwards through `setReorderRule`.
   */
  const create = async (body: CreateInventoryItemRequest): Promise<InventoryItemResponse> => {
    const { data } = await twineServiceClient.post<InventoryItemResponse, CreateInventoryItemRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.items.store'),
      body
    })

    return data.value as InventoryItemResponse
  }

  /**
   * Moves stock out of one location and into another, and answers both halves.
   *
   * Beside `adjust` rather than in the adjustment-groups collection: the subject is the same one
   * this collection already owns — stock at a location — and a move is what a merchant asks for,
   * while two adjustment groups are how it gets recorded. `adjust` above already crosses into
   * that domain for its request type, so this crosses for its response type.
   *
   * ⚠ **Two groups, and neither can be assumed to follow the other.** Both come back `pending`
   * and each is applied by its own queued reactor, so the caller polls each to a terminal status.
   * `DEC-2026-037` records why one endpoint writes both.
   *
   * ⚠ **`preventConcurrent` stays at its default `true`, and it is not a double-submit guard.**
   * The client aborts the *earlier* request and sends the new one, and an aborted POST already on
   * the wire is still processed — so a second press books a second move. The guard is the
   * caller's, exactly as it is for `adjust`.
   *
   * Rejects with an `Error` carrying the backend's `message` on a 422. Every shape that reaches
   * it is field-keyed by the request DTO: the same location at both ends, a soft-deleted location
   * at either end, and an empty item list. There is no bare-message exception on this path.
   */
  const move = async (body: MoveStockRequest): Promise<MoveStockResponse> => {
    const { data } = await twineServiceClient.post<MoveStockResponse, MoveStockRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.stock-moves.store'),
      body
    })

    return data.value as MoveStockResponse
  }

  return {
    adjust,
    create,
    move,
    setReorderRule
  }
}

/**
 * Compositions API Collection
 *
 * Backend Controllers: App\Admin\Http\Domains\Inventory\Controllers\Compositions\*
 *
 * A part list, and what happens when someone builds it or takes it apart. See
 * `domain/Inventory/Compositions/README.md`.
 *
 * ⚠ **The index answers JSON only because `CompositionIndexResponse` is a `MultiFormatResponse`.**
 * It rendered Inertia before that, so an AJAX read parsed HTML as JSON and yielded nothing,
 * silently and forever. `twineServiceClient` sends `Accept: application/json`, which is what
 * picks the JSON branch.
 *
 * ⚠ **Two producers, two shapes behind one type.** `index` serialises `CompositionDTO`.
 * `show`, `store` and `retire` return the Eloquent model, so they send the same keys plus
 * `updated_at` and `deleted_at`. `CompositionModel` declares the intersection, which is
 * everything any caller reads.
 *
 * `preventConcurrent` stays at its default `true` throughout. The token is derived from the
 * endpoint, the method and the query params, so `newestForParent` and `usedIn` never abort each
 * other — they differ in their params.
 */
export const callCompositions = () => {
  /** One request per hundred lists, so the common case of a handful is a single read. */
  const LIST_PAGE_SIZE = 100

  /** A refusal beats a silently partial history. 2,000 versions of one pack is already absurd. */
  const MAX_LIST_PAGES = 20

  /**
   * A compositions read, whole.
   *
   * ⚠ **`page: -1` does not work here.** `CompositionIndex` extends `EloquentBaseIndex`, which
   * never consults `wantsPagination()`, so the convention that serves `variantsByIds` would
   * reach Laravel's paginator as page `-1` rather than switch pagination off. Hence a loop.
   *
   * A partial read is the failure nobody notices: the history simply stops, and every figure
   * derived from it stays plausible. So an unreadable page throws rather than truncates.
   */
  const everyPageOf = async (filters: Record<string, string | number>): Promise<CompositionModel[]> => {
    const collected: CompositionModel[] = []
    let page = 1
    let lastPage = 1

    while (page <= lastPage) {
      if (page > MAX_LIST_PAGES) {
        throw new Error('There are too many part lists here to read in one go.')
      }

      const { data } = await twineServiceClient.get<PaginatedResponse<CompositionModel>>({
        endpoint: relativeZiggyRoute('admin.inventory.compositions.index'),
        queryParams: { ...filters, per_page: LIST_PAGE_SIZE, page }
      })

      const body = data.value

      // A nullish body without a throw is the client's dedup-abort path. It must read as a
      // failed load, never as an empty result — an empty history says a pack never changed.
      if (!body) throw new Error('The part lists could not be read.')

      collected.push(...body.data)
      lastPage = body.meta.last_page ?? page
      page += 1
    }

    return collected
  }

  /**
   * The newest part list for one parent variant, live or retired. Null when the parent has
   * never been a pack.
   *
   * ⚠ **No `filter[active]`, deliberately.** Retiring a list must not strand the packs already
   * on the shelf: opening one reverses the version it was built from, and the backend accepts a
   * retired version for exactly that reason. A read filtered to the live list answered null
   * after a retire, which took Open off every stock row. The caller reads `is_active` to tell
   * the two apart — a live list is always the newest, because a write retires the old version
   * before appending the new one.
   *
   * The index sorts by `created_at` descending, so the first row is the newest version.
   *
   * ⚠ `filter[parent]` matches `parent_id` alone. Both morph subjects hold `'variant'` today,
   * so nothing else can collide with it; the day a menu item holds stock, this needs
   * `filter[parent_type]` beside it.
   */
  const newestForParent = async (variantId: number): Promise<CompositionModel | null> => {
    const { data } = await twineServiceClient.get<PaginatedResponse<CompositionModel>>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.index'),
      queryParams: {
        'filter[parent]': variantId,
        'filter[type]': 'ASSEMBLY'
      }
    })

    return data.value?.data?.[0] ?? null
  }

  /**
   * The live lists among these parents — which of a set of variants are packs in their own right.
   *
   * ⚠ One request for the whole set. `filter[parent]` is an `ExactFilter`, which takes a repeated
   * parameter as a `whereIn`, so a list of twelve parts costs one call rather than twelve.
   *
   * Answers the lists, not the ids: a caller reads `parent_id` off each row. A variant with no
   * live list simply has no row here.
   */
  const livePacksAmong = async (parentVariantIds: number[]): Promise<CompositionModel[]> => {
    if (parentVariantIds.length === 0) return []

    const { data } = await twineServiceClient.get<PaginatedResponse<CompositionModel>>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.index'),
      queryParams: {
        'filter[parent]': parentVariantIds,
        'filter[type]': 'ASSEMBLY',
        'filter[active]': 1,
        per_page: LIST_PAGE_SIZE
      }
    })

    return data.value?.data ?? []
  }

  /** The live lists holding this variant as a part. Empty when it is used in none. */
  const usedIn = async (componentVariantId: number): Promise<CompositionModel[]> =>
    everyPageOf({
      'filter[component]': componentVariantId,
      'filter[active]': 1
    })

  /**
   * How much of each thing this variant's pack family holds, counting what is boxed.
   *
   * ⚠ **Not a compositions route.** It hangs off the variant, because the answer is about one
   * variant's shelf and the part lists are only how it is worked out. It is still gated on
   * `inventory.compositions.view` — the same fact the Pack card shows, arranged differently.
   *
   * ⚠ **An empty array is the ordinary answer, not a miss.** Most variants sit in no pack, and
   * the backend drops a leaf whose only contributor is itself rather than repeating the figure
   * the Stock by Location rows already print.
   *
   * One request for the whole family. The walk, the stock read and the arithmetic all happen
   * server-side, because a family is a graph and resolving it here would be a request per level.
   */
  const packFamily = async (variantId: number): Promise<FamilyStockLeaf[]> => {
    const { data } = await twineServiceClient.get<FamilyStockLeaf[]>({
      endpoint: relativeZiggyRoute('admin.variants.pack-family', { variant: variantId })
    })

    return data.value ?? []
  }

  /**
   * Every version of one parent's part list, newest first.
   *
   * ⚠ **Retired versions included, and that is the point.** A retired list is what the packs
   * already on the shelf were built from, so it is the only answer to "what did this pack used
   * to be made of". `newestForParent` answers one row; this answers the history.
   *
   * The index sorts by `created_at` descending, so the rows arrive newest first and a caller
   * needs no sort of its own.
   */
  const versionsForParent = async (variantId: number): Promise<CompositionModel[]> =>
    everyPageOf({
      'filter[parent]': variantId,
      'filter[type]': 'ASSEMBLY'
    })

  /** One list by id, with its lines loaded. A retired version answers here too. */
  const show = async (compositionId: string): Promise<CompositionModel> => {
    const { data } = await twineServiceClient.get<CompositionModel>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.show', { composition: compositionId })
    })

    return data.value as CompositionModel
  }

  /**
   * Writes a list. There is no update: every write appends a new version and retires the live
   * one, so the first write and each later one are the same call.
   *
   * Answers the new version **without** its lines — the action returns the model it created and
   * never loads the relation.
   *
   * ⚠ Nothing carries the version the editor started from, so a save overwrites whatever landed
   * in between. The backend has no guard for it.
   *
   * Rejects with an `Error` carrying the backend's `message` on a 422. Two shapes arrive there:
   * a field-keyed `errors` bag from the DTO (no lines, a run of zero), and a bare `message` from
   * `CompositionCannotBeWrittenException` — an unknown unit, a repeated part. That exception
   * carries no field at all, so a caller must not assume a bag.
   */
  const write = async (body: UpsertCompositionRequest): Promise<CompositionModel> => {
    const { data } = await twineServiceClient.post<CompositionModel, UpsertCompositionRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.store'),
      body
    })

    return data.value as CompositionModel
  }

  /**
   * Builds a run. Answers the id of the adjustment group it wrote.
   *
   * A run is all or nothing. Rejects with a 422 carrying the backend's message when the parts
   * cannot cover it, and nothing at all is written in that case.
   *
   * ⚠ `preventConcurrent` aborts the *earlier* request and sends the new one, and an aborted
   * POST already on the wire is still processed. The guard against building twice is the
   * caller's, exactly as it is for an adjustment.
   */
  const build = async (compositionId: string, body: RunCompositionRequest): Promise<string> => {
    const { data } = await twineServiceClient.post<string, RunCompositionRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.assemble', { composition: compositionId }),
      body
    })

    return data.value as string
  }

  /**
   * Opens a run of a named list version — including a retired one, which is the point. A retired
   * list is exactly what opens the packs built from it.
   */
  const open = async (compositionId: string, body: RunCompositionRequest): Promise<string> => {
    const { data } = await twineServiceClient.post<string, RunCompositionRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.disassemble', { composition: compositionId }),
      body
    })

    return data.value as string
  }

  /**
   * Stops a variant being a pack. Deactivates the live list without replacing it.
   *
   * ⚠ Not a delete. The row survives, because a retired list is what opens the packs already
   * built from it. Building is refused afterwards; opening is not.
   *
   * Rejects with an `Error` carrying the backend's `message` when the list is already retired.
   */
  const retire = async (compositionId: string): Promise<CompositionModel> => {
    const { data } = await twineServiceClient.post<CompositionModel, undefined>({
      endpoint: relativeZiggyRoute('admin.inventory.compositions.retire', { composition: compositionId }),
      body: undefined
    })

    return data.value as CompositionModel
  }

  /**
   * The variants a list names, in one call — their identity for a label, and their stock rows
   * for the buildable figure.
   *
   * ⚠ **A variants read, living in the inventory collection.** `callVariants().index` types its
   * `queryParams` to `q`, `page` and `include`, so neither `filter[ids]` nor `per_page` fits
   * through it. Widening that belongs to the variants domain; until it happens, the compositions
   * screens read here.
   *
   * ⚠ **`admin.variants.index` is served by the `@deprecated` `VariantIndex`, not by
   * `ProductVariantIndex`.** The canonical index answers the Flutter inventory app instead, and
   * a filter added there is invisible to the admin.
   *
   * ⚠ **No `stock_levels` on these rows.** `SimpleInventoryItemDTO` omits it on purpose — it sits
   * inside a stored event, and a derived figure does not belong in one. So the owned figure a
   * build reads is `available_qty - (consignment_qty ?? 0)`, per location, and `composed` is not
   * available from this endpoint at all. Reaching for `stock_levels` here yields `undefined`
   * rather than an error.
   *
   * ⚠ **Owned, never sellable.** Sellable stock includes goods a supplier still owns on
   * consignment, and a build must not box those. The backend guards on `owned`, so a preview
   * built from sellable promises a run that is then refused.
   *
   * An empty id set answers without a request — there is nothing to ask for.
   *
   * `include` is deliberately absent: `SimpleVariantDTO::$inventoryItems` is not lazy and
   * `VariantIndex::$relations` eager-loads the rows and their locations, so the stock rows and
   * `location` arrive on every call.
   *
   * ⚠ The concurrency token derives from the endpoint and the params, so two callers asking for
   * the *same* ids abort each other. The card and the build panel share one query rather than
   * each firing this.
   */
  const variantsByIds = async (variantIds: number[]): Promise<IndexProductVariant[]> => {
    if (variantIds.length === 0) {
      return []
    }

    const { data } = await twineServiceClient.get<PaginatedResponse<IndexProductVariant>>({
      endpoint: relativeZiggyRoute('admin.variants.index'),
      queryParams: {
        'filter[ids]': variantIds.join(','),
        // `page: -1` asks for one page holding every part. It is no longer what makes the
        // read correct: `VariantIndex` sends `filter[ids]` to the search engine now, so the
        // engine narrows to the parts before it cuts a window. It stays because a part list
        // is read whole, and paging it would be one request per twenty parts.
        page: -1
      }
    })

    return data.value?.data ?? []
  }

  return {
    build,
    livePacksAmong,
    newestForParent,
    open,
    packFamily,
    retire,
    show,
    usedIn,
    variantsByIds,
    versionsForParent,
    write
  }
}
